import { COMMON_FRAME_RATES, type Rational } from '@ve/core';
import { Clapperboard, FolderOpen, FolderPlus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { tip } from '../../lib/tooltip.ts';
import { projectStore } from '../../projects/setup.ts';
import type { PickedLocation, RecentProject } from '../../projects/types.ts';
import {
  createProjectAt,
  openProjectAt,
  openRecentProject,
  pickProjectLocation,
} from '../../state/session.ts';
import { errorMessage, toast } from '../../state/toast-store.ts';
import { dismissPendingLaunch, openPendingLaunch, usePendingLaunch } from '../../state/launch.ts';

export const RESOLUTION_PRESETS = [
  { label: '4K UHD (3840×2160)', width: 3840, height: 2160 },
  { label: '1080p (1920×1080)', width: 1920, height: 1080 },
  { label: '720p (1280×720)', width: 1280, height: 720 },
  { label: 'Vertical 1080×1920', width: 1080, height: 1920 },
  { label: 'Square 1080×1080', width: 1080, height: 1080 },
  { label: 'Portrait 4:5 1080×1350', width: 1080, height: 1350 },
];

function timeAgo(ms: number): string {
  const s = Math.round((Date.now() - ms) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(ms).toLocaleDateString();
}

export function ProjectPicker({ highlightId }: { highlightId?: string | null }) {
  const [recents, setRecents] = useState<RecentProject[] | null>(null);
  const [name, setName] = useState('Untitled project');
  const [preset, setPreset] = useState(1);
  const [fpsIndex, setFpsIndex] = useState(4);
  const [busy, setBusy] = useState(false);
  /** A picked folder without a project: offer to create one in place. */
  const [emptyFolder, setEmptyFolder] = useState<PickedLocation | null>(null);

  const refresh = () =>
    projectStore.listRecent().then(setRecents, (err: unknown) => {
      setRecents([]);
      toast.error(`Could not read recent projects: ${errorMessage(err)}`);
    });

  useEffect(() => {
    void refresh();
  }, []);

  const settings = () => {
    const { width, height } = RESOLUTION_PRESETS[preset]!;
    const fps: Rational = COMMON_FRAME_RATES[fpsIndex]!.fps;
    return { width, height, fps };
  };

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      toast.error(errorMessage(err));
      void refresh();
    } finally {
      setBusy(false);
    }
  };

  const createNew = () =>
    run(async () => {
      const location = await pickProjectLocation('create');
      if (!location) return;
      await createProjectAt(location, { name, settings: settings() });
    });

  const openFolder = () =>
    run(async () => {
      const location = await pickProjectLocation('open');
      if (!location) return;
      if (location.hasProject) await openProjectAt(location);
      else {
        setName(location.name);
        setEmptyFolder(location);
      }
    });

  const createInPlace = () =>
    run(async () => {
      if (!emptyFolder) return;
      await createProjectAt(emptyFolder, { name, settings: settings() }, { inPlace: true });
    });

  const launch = usePendingLaunch((st) => st.pending);
  const highlighted = launch ? undefined : recents?.find((r) => r.id === highlightId);

  return (
    <div className="picker">
      <div className="picker-brand">
        <Clapperboard size={28} />
        <h1>Cutroom</h1>
      </div>
      {launch && (
        <div className="picker-reopen">
          <span>
            Open <strong>{launch.project.name}</strong>?{' '}
            {launch.project.access === 'missing'
              ? `Choose the folder that contains “${launch.project.fileName}” — the picker starts there, so just confirm it. Its media and exports live in that folder.`
              : 'Your browser needs permission to open its folder again.'}
          </span>
          <button
            className="btn primary"
            disabled={busy}
            onClick={() => void run(openPendingLaunch)}
            {...tip(
              launch.project.access === 'missing'
                ? `Pick the folder of “${launch.project.fileName}” to open the project with its media`
                : `Allow access to the project folder and open “${launch.project.name}”`,
            )}
          >
            {launch.project.access === 'missing' ? 'Choose folder…' : 'Open'}
          </button>
          <button
            className="btn ghost icon"
            disabled={busy}
            onClick={dismissPendingLaunch}
            {...tip('Don’t open this project')}
          >
            <X size={14} />
          </button>
        </div>
      )}
      {highlighted && (
        <div className="picker-reopen">
          <span>
            Continue editing <strong>{highlighted.name}</strong>? Your browser needs permission to open its
            folder again.
          </span>
          <button
            className="btn primary"
            disabled={busy}
            onClick={() => void run(() => openRecentProject(highlighted.id))}
            {...tip(`Reopen “${highlighted.name}” from the folder “${highlighted.location}”`)}
          >
            Reopen
          </button>
        </div>
      )}
      <div className="picker-grid">
        <section className="panel">
          <div className="panel-header">
            {emptyFolder ? `New project in “${emptyFolder.name}”` : 'New project'}
          </div>
          <div className="dialog-body form-grid">
            {emptyFolder && (
              <p className="picker-note">
                This folder has no Cutroom project yet. A project created here can use the media already in
                the folder without copying it.
              </p>
            )}
            <label htmlFor="pname">Name</label>
            <input
              id="pname"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void (emptyFolder ? createInPlace() : createNew())}
            />
            <label htmlFor="pres">Resolution</label>
            <select
              id="pres"
              className="select"
              value={preset}
              onChange={(e) => setPreset(Number(e.target.value))}
            >
              {RESOLUTION_PRESETS.map((p, i) => (
                <option key={p.label} value={i}>
                  {p.label}
                </option>
              ))}
            </select>
            <label htmlFor="pfps">Frame rate</label>
            <select
              id="pfps"
              className="select"
              value={fpsIndex}
              onChange={(e) => setFpsIndex(Number(e.target.value))}
            >
              {COMMON_FRAME_RATES.map((r, i) => (
                <option key={r.label} value={i}>
                  {r.label} fps
                </option>
              ))}
            </select>
            <span />
            {emptyFolder ? (
              <div className="row">
                <button
                  className="btn primary"
                  disabled={busy}
                  onClick={() => void createInPlace()}
                  {...tip(`Create the project file inside “${emptyFolder.name}”`)}
                >
                  <FolderPlus size={14} /> Create here
                </button>
                <button
                  className="btn ghost"
                  disabled={busy}
                  onClick={() => setEmptyFolder(null)}
                  {...tip('Back')}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                className="btn primary"
                disabled={busy}
                onClick={() => void createNew()}
                {...tip(
                  'Choose where to put it — a new folder named after the project is created there',
                  'Enter',
                )}
              >
                <FolderPlus size={14} /> Create project…
              </button>
            )}
          </div>
        </section>
        <section className="panel">
          <div className="panel-header">
            Projects
            <span className="spacer" />
            <button
              className="btn small"
              disabled={busy}
              onClick={() => void openFolder()}
              {...tip('Open a project folder from your disk (or a folder of footage to start a project in)')}
            >
              <FolderOpen size={13} /> Open folder…
            </button>
          </div>
          <div className="panel-body picker-list">
            {recents === null && <div className="empty">Loading…</div>}
            {recents?.length === 0 && (
              <div className="empty">
                No recent projects on this device.
                <br />
                Create one, or open an existing project folder.
              </div>
            )}
            {recents?.map((p) => (
              <div
                key={p.id}
                className={`picker-item${p.id === highlightId ? ' highlight' : ''}`}
                onClick={() => !busy && void run(() => openRecentProject(p.id))}
                {...tip(`Open “${p.name}” (folder “${p.location}”)`)}
              >
                <div>
                  <div className="picker-item-name">{p.name}</div>
                  <div className="muted">
                    {p.location} · opened {timeAgo(p.lastOpened)}
                  </div>
                </div>
                <button
                  className="btn ghost icon"
                  {...tip('Remove from this list (the folder and its files stay on your disk)')}
                  onClick={(e) => {
                    e.stopPropagation();
                    void projectStore.forgetRecent(p.id).then(refresh);
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
